import React, { createContext, useState, useContext } from 'react';

type Modal = {
  id: string;
  title: string;
  body: React.ReactNode;
  footer?: React.ReactNode;
  show: boolean;
  size?: 'sm' | 'lg' | 'xl' | undefined;
  theme?: 'light' | 'dark';
};

type ModalContextType = {
  modals: Modal[];
  addModal: (modal: Omit<Modal, 'id' | 'show'>) => void;
  removeModal: (id?: string) => void;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [modals, setModals] = useState<Modal[]>([]);

  const addModal = (modal: Omit<Modal, 'id' | 'show'>) => {
    const id = Math.random().toString(36).substring(7);
    setModals((prevModals) => [...prevModals, { ...modal, id, show: true }]);
  };

  const removeModal = (id?: string) => {
    if (id) {
      setModals((prevModals) => prevModals.filter((modal) => modal.id !== id));
    } else {
      setModals((prevModals) => [...prevModals.slice(0, -1)]);
    }
  };

  return (
    <ModalContext.Provider value={{ modals, addModal, removeModal }}>
      {children}
    </ModalContext.Provider>
  );
};

export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModalContext must be used within a ModalProvider');
  }
  return context;
};
